// StepDeviceVerify — Step 1.1 WebAuthn assertion renderer.
// Spec: docs/specs/location-tasks.md §8.4.1.
//
// Wired into ScanWizardPage's <Step1Verify renderDevice={...} />.

import React, { useState } from "react";
import { Fingerprint, Loader2, AlertTriangle } from "lucide-react";
import { startAuthentication } from "@simplewebauthn/browser";
import { useLiff } from "../../../liff/index.js";
import { apiFetch } from "../../../lib/api-client.js";
import type { WizardData } from "./types.js";

type AuthOptionsJSON = Parameters<typeof startAuthentication>[0]["optionsJSON"];

interface Props {
  data: WizardData;
  onComplete: () => void;
}

export default function StepDeviceVerify({ data, onComplete }: Props): React.ReactElement {
  const { session } = useLiff();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async (): Promise<void> => {
    if (!session?.session_token) return;
    setError(null);
    setBusy(true);
    try {
      const startResp = await apiFetch(
        `/v1/patrols/submissions/${encodeURIComponent(data.submission.id)}/verify/device/start`,
        { method: "POST" },
        { jwt: session.session_token },
      );
      const startBody = (await startResp.json().catch(() => ({}))) as { options?: AuthOptionsJSON; error?: string };
      if (!startResp.ok || !startBody.options) {
        setError(startBody.error ?? `HTTP ${startResp.status}`);
        return;
      }
      const assertion = await startAuthentication({ optionsJSON: startBody.options });
      const finishResp = await apiFetch(
        `/v1/patrols/submissions/${encodeURIComponent(data.submission.id)}/verify/device/finish`,
        { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ assertionResponse: assertion }) },
        { jwt: session.session_token },
      );
      const finishBody = (await finishResp.json().catch(() => ({}))) as { ok?: boolean; error?: string };
      if (!finishResp.ok || !finishBody.ok) {
        setError(finishBody.error ?? `HTTP ${finishResp.status}`);
        return;
      }
      onComplete();
    } catch (e) {
      setError((e as Error).message ?? "device_verify_failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2">
      <button
        onClick={run}
        disabled={busy}
        className="block w-full h-12 rounded-xl bg-accent text-text-inverse font-semibold text-sm disabled:bg-bg-raised disabled:text-text-muted"
      >
        {busy ? (
          <span className="inline-flex items-center justify-center gap-1"><Loader2 className="h-4 w-4 animate-spin" /> 等待 passkey 簽署…</span>
        ) : (
          <span className="inline-flex items-center justify-center gap-1"><Fingerprint className="h-4 w-4" /> 使用此裝置驗證</span>
        )}
      </button>
      {error && (
        <p className="text-xs text-danger inline-flex items-start gap-1">
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5" />
          {error === "no_passkey_enrolled"
            ? "您尚未在此裝置註冊 passkey，請先聯絡管理員開啟註冊連結。"
            : error === "credential_not_recognised"
              ? "此 passkey 不屬於您的帳號。"
              : `驗證失敗：${error}`}
        </p>
      )}
    </div>
  );
}
